import { Container, Grid, Theme, useMediaQuery, styled, TableRow, Button, TableCell, Table } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import SettingsSidebar from "./SettingsSidebar";
import Verification from "./tabs/Verification";
import Capturing from "./tabs/Capturing";
import About from "./tabs/About";
import { FVAdvancedInitParams, FVInitParams } from "neurotec-megamatcherid-management-client";
import ICAO from "./tabs/ICAO";
import Liveness from "./tabs/Liveness";
import Management from "./tabs/Management";
import Reset from "./tabs/Reset";
import { ISliderInterface } from "./components/SliderCell";
import { initialFvParameters, useLocalStorage } from "../../helpers/useLocalStorage";
import { mmidInitParamsStorage } from "../../config/const";
import { AppContext } from "../../store/context";
import { useSnackbar } from "notistack";
import { ActionType } from "../../components/Navigation/store/actions";
import { NavigationContext } from "../../components/Navigation/store/context";
import { makeStyles } from "@mui/styles";
import Developer from "./tabs/Developer";
import Attributes from "./tabs/Attributes";
import Detection from "./tabs/Detection";
import FaceQuality from "./tabs/FaceQuality";
import Tracking from "./tabs/Tracking";

const GridBar = styled(Grid)(() => ({
    paddingTop: "2.1rem",
    position: "sticky",
    top: "max(7vh, 50px)",
    height: "fit-content"
}));

const GridTab = styled(Grid)(({ theme }) => ({
    paddingTop: "2rem",
    paddingLeft: "2rem",
    [theme.breakpoints.down("md")]: {
        paddingLeft: 0
    }
}));

export const SettingContainer = styled(Container)(({ theme }) => ({
    maxWidth: "97vw",
    [theme.breakpoints.down("md")]: {
        maxWidth: "100%"
    }
}));

export const SettingTable = styled(Table)(() => ({
    tableLayout: "fixed"
}))

export const settingUseStyles = makeStyles(() => ({
    container: {
        marginBottom: "2rem",
        tableLayout: "fixed"
    }
}))

export const SmallTextTableRow = styled(TableRow)(() => ({
    borderBottom: "none"
}))

export const SmallContentTableRow = styled(TableRow)(() => ({
    paddingLeft: "16px"
}))

export const NormalTableRow = styled(TableRow)(() => ({
    paddingLeft: "0px"
}))

export const DefaultButton = styled(Button)(({theme}) => ({
    [theme.breakpoints.down("sm")]: {
        paddingLeft: "0px",
        paddingRight: "0px"
    }
}))

export const TableCellButton = styled(TableCell)(({theme}) => ({
    [theme.breakpoints.down("sm")]: {
        paddingLeft: "0px",
    }
}))

export enum SettingType {
    CAPTURING = "CAPTURING",
    DETECTION = "DETECTION",
    TRACKING = "TRACKING",
    FACE_QUALITY = "FACE_QUALITY",
    VERIFICATION = "VERIFICATION",
    LIVENESS = "LIVENESS",
    ATTRIBUTES = "ATTRIBUTES",
    ICAO = "ICAO",
    MANAGEMENT = "MANAGEMENT",
    DEVELOPER = "DEVELOPER",
    RESET = "RESET",
    ABOUT = "ABOUT",
    ACCOUNTS = "ACCOUNTS",
    ADJUDICATION = "ADJUDICATION"
}

const Settings: React.FC = () => {

    const { enqueueSnackbar } = useSnackbar();
    const { state, dispatch } = useContext(NavigationContext)
    const [setting, setSetting] = useState<SettingType>(state.settingType)
    const [options, setOptions] = useLocalStorage<FVInitParams>(mmidInitParamsStorage, initialFvParameters);
    const [qualityImageSize, setQualityImageSize] = useState<[number, number]>([960, 540])
    const appState = useContext(AppContext).state
    const breakpoint = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

    useEffect(() => {
        setSetting(state.settingType)
    }, [enqueueSnackbar, state])

    const setSettingType = (type: SettingType) => {
        setSetting(type)
        dispatch({type: ActionType.SetSettingType, settingType: type})
    }

    const findDefaultOption = (option: string): FVAdvancedInitParams | undefined => {
        return appState.defaultOptions.find((el) => el.key === option)
    }

    const findOption = (option: string, advanced?: boolean) : FVAdvancedInitParams | undefined => {
        return options.advancedParameters?.find((el) => el.key === option)
    }

    const convertAdvancedToSliderInterface = (param: FVAdvancedInitParams) => {
        let key: string | undefined = undefined
        let value: number | undefined = undefined
        
        if (param.key)
            key = param.key
        if (param.value || param.value as unknown as number === 0)
            value = Number(param.value)
        if (key === undefined || value === undefined) {
            enqueueSnackbar("Could not convert advanced option", {variant: "error"})
            return undefined
        }
            
        return {key: key, value: value}
    }

    const findFVSliderInterface = (param: string): ISliderInterface | undefined => {
        if (!options.advancedParameters)
            return undefined
        let par = findOption(param)
        if (par) {
            return convertAdvancedToSliderInterface(par)
        } else {
            if (!appState.defaultOptions || appState.defaultOptions.length === 0)
                return undefined
            par = findDefaultOption(param)
            if (par) {
                return convertAdvancedToSliderInterface(par)
            } else {
                enqueueSnackbar("Could not get default properties", {variant: "error"})
            }
            return undefined
        }
    }

    const findDefaultFVSliderInterface = (param: string): ISliderInterface | undefined => {
        let par = findDefaultOption(param)
        if (par) {
            return convertAdvancedToSliderInterface(par)
        } else {
            return undefined
        }
    }

    const findFVStringSetting = (param: string): string => {
        let par = findOption(param)
        if (par) {
            return par.value ?? ""
        } else {
            if (!appState.defaultOptions || appState.defaultOptions.length === 0)
                return ""
            par = findDefaultOption(param)
            if (par) {
                return par.value ?? ""
            } else {
                enqueueSnackbar("Could not get default properties", {variant: "error"})
            }
            return ""
        }
    }

    const findFVBooleanSetting = (param: string): boolean => {
        let par = findOption(param)
        if (par) {
            return par.value === "true"? true : false
        } else {
            if (!appState.defaultOptions || appState.defaultOptions.length === 0)
                return false
            par = findDefaultOption(param)
            if (par) {
                return par.value === "true"? true : false
            } else {
                enqueueSnackbar("Could not get default properties", {variant: "error"})
            }
            return false
        }
    }

    const setFVSliderInterfaceOption = (op: ISliderInterface, basic?: boolean | undefined) => {
        setOption(op.key, op.value, basic)
    }

    const setOption = (key: string, value: string | number | boolean, basic?: boolean) => {
        switch (typeof value) {
            case "string":
                basic? setNewBasicOption(key, value) : setNewOptions(key, value)
                return;
            case "number":
                basic? setNewBasicOption(key, value.toString()) : setNewOptions(key, value.toString())
                return;
            case "boolean":
                basic? setNewBasicOption(key, value? "true" : "false") : setNewOptions(key, value? "true" : "false")
                return;
        }
    }

    const setNewBasicOption = (key: string, value: string) => {
        switch (key) {
            case "timeout":
                if (options.timeout !== Number(value)) setOptions({...options, timeout: Number(value)})
                break
            case "livenessMode":
                if (options.livenessMode !== Number(value)) setOptions({...options, livenessMode: Number(value)})
                break
            case "checkIcaoCompliance":
                setOptions({...options, checkIcaoCompliance: value === "true" ? true : false})
        }
    } 

    const setNewOptions = (key: string, value: string) => {
        let par = findOption(key)
        if (par) {
            par.value = value.toString();
            setOptions({...options, advancedParameters: options.advancedParameters})
        } else {
            setNewAdvancedOption(key, value.toString())
        }
    }

    const setNewAdvancedOption = (key: string, value: string) => {
        if (options.advancedParameters)
            setOptions({...options, advancedParameters: [...options.advancedParameters, {key: key, value: value}]})
    }

    const removeAdvancedSetting = (key: string) => {
        setOptions({...options, advancedParameters: options.advancedParameters?.filter((par) => {
            return par.key !== key
        })})
    }

    const resetValueToDefault = (name: string, basic?: boolean) => {
        if (basic)
            resetBasicValueToDefault(name)
        else
            resetAdvanceValueToDefault(name)
    }

    const resetBasicValueToDefault = (key: string) => {
        switch (key) {
            case "timeout":
                setNewBasicOption(key, "0")
                break
            case "livenessMode":
                setNewBasicOption(key, "1")
                break
            case "checkIcaoCompliance":
                setNewBasicOption(key, "false")
                break
        }
    }

    const resetAdvanceValueToDefault = (name: string) => {
        let defaultPar = findDefaultOption(name)
        if (defaultPar) {
            if (defaultPar.key && defaultPar.value) {
                let par = findOption(name)
                if (par) {
                    par.value = defaultPar.value
                    setOptions({...options, advancedParameters: options.advancedParameters})
                }
            }
            if (name === "IcaoWarningsFilter") {
                let par = findOption(name)
                if (par) {
                    if (defaultPar.value){
                        par.value = defaultPar.value
                    } else {
                        par.value = ""
                    }
                    setOptions({...options, advancedParameters: options.advancedParameters})
                }
            }
        } else {
            enqueueSnackbar("Could not get default properties", {variant: "error"})
        }
    }

    const resetAllParametersToDefault = () => {
        setOptions(initialFvParameters)
    }

    const handleQualityImageSetting = (w: number, h: number) => {
        setQualityImageSize([w, h])
        let par = options.advancedParameters?.find((e) => e.key === "StrictQualityImageSize")
        if (par) {
            const nextOptions = options.advancedParameters?.map((p) => {
                if (p.key === "StrictQualityImageSize")
                    return {key: "StrictQualityImageSize", value: (w * h).toString()}
                else {
                    return p
                }
            })
            setOptions({...options, advancedParameters: nextOptions})
        } else {
            if (options.advancedParameters)
                setOptions({...options, advancedParameters: [...options.advancedParameters, {key: "StrictQualityImageSize", value: (w * h).toString()}]})
        }
    }

    const getTimeout = () => {
        let par = options.timeout
        if (par !== undefined) {
            return Number(par)
        }
        else {
            setOption("timeout", "0", true)
            return 0
        }
    }

    const getLiveness = (): string => {
        let par = options.livenessMode
        if (par !== undefined) {
            return par.toString()
        }
        setOption("livenessMode", "0", true)
        return "0"
    }

    const getIcao = (): boolean => {
        let par = options.checkIcaoCompliance
        if (par !== undefined) {
            return par
        }
        setOption("checkIcaoCompliance", false, true)
        return false
    }

    const getLivenessActionSequence = (): string => {
        let par = findOption("LivenessCustomActionSequence")?.value
        if (par !== undefined) {
            return par;
        } else {
            return ""
        }
    }

    const allowFaceToDisappear = () => {
        let par = findOption("AllowFaceToDisappear")?.value
        if (par !== undefined) {
            return par === "true" ? true : false
        }
        return false
    }

    const backgroundRemoval = () => {
        let par = findOption("IcaoRemoveBackground")?.value
        if (par !== undefined) {
            return par === "true" ? true : false
        }
        return false
    }

    const icaoWarningFilter = () => {
        let par = findOption("IcaoWarningsFilter")?.value
        if (par !== undefined) {
            return par
        }
        return ""
    }

    const attributesActive = (): boolean => {
        return findFVBooleanSetting("DetectProperties")
    }

    const getStrictQualityImageSize = () => {
        let par = findOption("StrictQualityImageSize")?.value
        if (par !== undefined) {
            return [qualityImageSize[0], qualityImageSize[1], Number(par)] as [number, number, number]
        }
        return [...qualityImageSize, qualityImageSize[0] * qualityImageSize[1]] as [number, number, number]
    }

    const getTab = (type: SettingType) => {
        switch(type) {
            case SettingType.CAPTURING:
                return <Capturing 
                            findDefaultFVSliderInterface={findDefaultFVSliderInterface}
                            findFVSliderInterface={findFVSliderInterface}
                            setFVSliderInterfaceOption={setFVSliderInterfaceOption} 
                            resetValueToDefault={resetValueToDefault}
                            timeout={getTimeout()}
                            setOption={setOption}
                            handleQualityImageSetting={handleQualityImageSetting}
                            qualityImageSize={getStrictQualityImageSize()}
                            icaoWarningFilter={icaoWarningFilter()}
                            backgroundRemoval={backgroundRemoval()}
                            findFVStringSetting={findFVStringSetting}
                        />
            case SettingType.DETECTION:
                return <Detection
                            findDefaultFVSliderInterface={findDefaultFVSliderInterface}
                            findFVSliderInterface={findFVSliderInterface}
                            setFVSliderInterfaceOption={setFVSliderInterfaceOption}
                            resetValueToDefault={resetValueToDefault}                       
                        />
            case SettingType.TRACKING:
                return <Tracking
                            findDefaultFVSliderInterface={findDefaultFVSliderInterface}
                            findFVSliderInterface={findFVSliderInterface}
                            setFVSliderInterfaceOption={setFVSliderInterfaceOption}
                            resetValueToDefault={resetValueToDefault}
                            findFVBooleanSetting={findFVBooleanSetting}
                            allowFaceToDisappear={allowFaceToDisappear()}
                            setOption={setOption}
                        />
            case SettingType.FACE_QUALITY:
                return <FaceQuality
                            findDefaultFVSliderInterface={findDefaultFVSliderInterface}
                            findFVSliderInterface={findFVSliderInterface}
                            setFVSliderInterfaceOption={setFVSliderInterfaceOption}
                            resetValueToDefault={resetValueToDefault}
                            setOption={setOption}
                            icaoWarningFilter={icaoWarningFilter()}
                        />
            case SettingType.VERIFICATION:
                return <Verification 
                            findDefaultFVSliderInterface={findDefaultFVSliderInterface} 
                            findFVSliderInterface={findFVSliderInterface} 
                            setFVSliderInterfaceOption={setFVSliderInterfaceOption} 
                            resetValueToDefault={resetValueToDefault}
                        />
            case SettingType.LIVENESS:
                return <Liveness
                            options={options}
                            liveness={getLiveness()}
                            findDefaultFVSliderInterface={findDefaultFVSliderInterface}
                            findFVSliderInterface={findFVSliderInterface}
                            setFVSliderInterfaceOption={setFVSliderInterfaceOption}
                            setOption={setOption}
                            resetValueToDefault={resetValueToDefault}
                            livenessCustomActionSequence={getLivenessActionSequence()}
                            handleQualityImageSetting={handleQualityImageSetting}
                            findFVStringSetting={findFVStringSetting}
                        />
            case SettingType.ATTRIBUTES:
                return <Attributes
                            findDefaultFVSliderInterface={findDefaultFVSliderInterface}
                            findFVSliderInterface={findFVSliderInterface}
                            setFVSliderInterfaceOption={setFVSliderInterfaceOption}
                            resetValueToDefault={resetValueToDefault}
                            attributesActive={attributesActive()}
                            icaoCompliance={getIcao()}
                            setOption={setOption}
                        />
            case SettingType.ICAO:
                return <ICAO 
                            findDefaultFVSliderInterface={findDefaultFVSliderInterface}
                            findFVSliderInterface={findFVSliderInterface}
                            setFVSliderInterfaceOption={setFVSliderInterfaceOption}
                            resetValueToDefault={resetValueToDefault}
                            setOption={setOption}
                            icaoCompliance={getIcao()}
                            backgroundRemoval={backgroundRemoval()}
                        />
            case SettingType.MANAGEMENT:
                return <Management hasClear/>
            case SettingType.DEVELOPER:
            return <Developer
                            setNewOption={setNewAdvancedOption}
                            options={options.advancedParameters ?? []}
                            removeOption={removeAdvancedSetting}    
                        />
            case SettingType.RESET:
                return <Reset reset={resetAllParametersToDefault}/>
            case SettingType.ABOUT:
                return <About/>
            default:
                return <Capturing
                            findDefaultFVSliderInterface={findDefaultFVSliderInterface}
                            findFVSliderInterface={findFVSliderInterface}
                            setFVSliderInterfaceOption={setFVSliderInterfaceOption} 
                            timeout={getTimeout()}
                            resetValueToDefault={resetValueToDefault}
                            setOption={setOption}
                            handleQualityImageSetting={handleQualityImageSetting}
                            qualityImageSize={getStrictQualityImageSize()}
                            icaoWarningFilter={icaoWarningFilter()}
                            backgroundRemoval={backgroundRemoval()}
                            findFVStringSetting={findFVStringSetting}
                        />
        }
    }

    return(
        <SettingContainer>
            <Grid container style={{position: "relative"}}>
                <GridBar item md={2.5} xs={0}>
                {breakpoint?
                    <SettingsSidebar activeSetting={setting} setSettings={setSettingType} />
                    :
                    null
                }
                </GridBar>
                <GridTab item md={9.5} xs={12}>
                    {getTab(setting)}
                </GridTab>
            </Grid>
        </SettingContainer>
    );
}

export default Settings